import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { RangeSelection, TranslationState, WordToken } from './types';
import { calculateDistance, findSentenceBoundaries, type SelectionResult } from './selection-algorithm';

interface UseSelectionOptions {
  tokens: WordToken[];
  sourceLanguage?: string;
  targetLanguage?: string;
  chunkText: string;
  onTranslationRequest: (selection: RangeSelection) => void;
  /** Initial selections restored from history */
  initialSelections?: Map<string, RangeSelection>;
  /** Called when selections change. */
  onSelectionsChange?: (selections: Map<string, RangeSelection>) => void;
  /** Bumped on clear — triggers re-sync from initialSelections */
  revision?: number;
}

export function useSelection({
  tokens,
  sourceLanguage,
  targetLanguage,
  onTranslationRequest,
  initialSelections,
  onSelectionsChange,
  revision,
}: UseSelectionOptions) {
  const [selections, setSelections] = useState<Map<string, RangeSelection>>(
    () => initialSelections ?? new Map()
  );

  // Re-sync from initialSelections when revision changes (clear).
  // Marks the upcoming selection change as a revision reset so the notify
  // effect below skips it — otherwise the sync would bounce back as a
  // duplicate change notification.
  const prevRevisionRef = useRef(revision);
  const isRevisionReset = useRef(false);
  useEffect(() => {
    if (revision !== prevRevisionRef.current) {
      prevRevisionRef.current = revision;
      isRevisionReset.current = true;
      setSelections(initialSelections ?? new Map());
    }
  }, [revision, initialSelections]);

  // Notify parent of selection changes.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isRevisionReset.current) {
      isRevisionReset.current = false;
      return;
    }
    onSelectionsChange?.(selections);
  }, [selections]);

  // Pre-compute punctuation markers
  const punctuationMarkers = useMemo(
    () => tokens.map((t) => /[.!?;:]$/.test(t.text)),
    [tokens]
  );

  const getSentenceContext = useCallback(
    (startIndex: number, endIndex: number): string => {
      const sentenceEnders = new Set(['.', '!', '?', '\u2026', '\u3002', '\uFF01', '\uFF1F']);

      let sentenceStart = 0;
      for (let i = startIndex - 1; i >= 0; i--) {
        const text = tokens[i].text.trim();
        if (text.length > 0 && sentenceEnders.has(text[text.length - 1])) {
          sentenceStart = i + 1;
          break;
        }
      }

      let sentenceEnd = tokens.length - 1;
      for (let i = endIndex; i < tokens.length; i++) {
        const text = tokens[i].text.trim();
        if (text.length > 0 && sentenceEnders.has(text[text.length - 1])) {
          sentenceEnd = i;
          break;
        }
      }

      return tokens
        .slice(sentenceStart, sentenceEnd + 1)
        .map((t) => t.text)
        .join(' ');
    },
    [tokens]
  );

  const getSelectionContaining = useCallback(
    (nodeIndex: number): RangeSelection | undefined => {
      for (const sel of selections.values()) {
        if (nodeIndex >= sel.startIndex && nodeIndex <= sel.endIndex) return sel;
      }
      return undefined;
    },
    [selections]
  );

  const extractRangeText = useCallback(
    (start: number, end: number): string => {
      return tokens
        .slice(start, end + 1)
        .map((t) => t.text)
        .join(' ');
    },
    [tokens]
  );

  const createSelection = useCallback(
    (startIndex: number, endIndex: number): RangeSelection => {
      const text = extractRangeText(startIndex, endIndex);
      const isSingleWord = startIndex === endIndex;
      const preloaded = isSingleWord ? tokens[startIndex]?.translation : undefined;
      const sentenceContext = getSentenceContext(startIndex, endIndex);

      return {
        startIndex,
        endIndex,
        selectionId: `sel_${startIndex}_${endIndex}_${Date.now()}`,
        text,
        translation: preloaded,
        translationState: preloaded ? 'translated' : (isSingleWord ? 'notTranslated' : 'loading'),
        sourceLanguage,
        targetLanguage,
        chunkText: sentenceContext,
        partOfSpeech: isSingleWord ? tokens[startIndex]?.partOfSpeech : undefined,
        normalizedForm: isSingleWord ? tokens[startIndex]?.normalizedForm : undefined,
      };
    },
    [tokens, sourceLanguage, targetLanguage, getSentenceContext, extractRangeText]
  );

  const applyResult = useCallback(
    (result: SelectionResult) => {
      setSelections((prev) => {
        const next = new Map(prev);

        switch (result.action) {
          case 'createNew':
            if (result.newSelection) next.set(result.newSelection.selectionId, result.newSelection);
            break;
          case 'extend':
            if (result.oldSelection) next.delete(result.oldSelection.selectionId);
            if (result.newSelection) next.set(result.newSelection.selectionId, result.newSelection);
            break;
          case 'bridge':
            if (result.leftSelection) next.delete(result.leftSelection.selectionId);
            if (result.rightSelection) next.delete(result.rightSelection.selectionId);
            if (result.mergedSelection) next.set(result.mergedSelection.selectionId, result.mergedSelection);
            break;
          case 'deselect':
            if (result.oldSelection) next.delete(result.oldSelection.selectionId);
            break;
        }

        return next;
      });

      // Request translation for new/merged selections (including preloaded)
      const sel = result.newSelection || result.mergedSelection;
      if (sel) {
        onTranslationRequest(sel);
      }
    },
    [onTranslationRequest]
  );

  const handleTap = useCallback(
    (nodeIndex: number) => {
      const existing = getSelectionContaining(nodeIndex);
      if (existing) {
        applyResult({ action: 'deselect', oldSelection: existing });
      } else {
        const newSel = createSelection(nodeIndex, nodeIndex);
        applyResult({ action: 'createNew', newSelection: newSel });
      }
    },
    [getSelectionContaining, createSelection, applyResult]
  );

  const handleLongPress = useCallback(
    (nodeIndex: number) => {
      const selArray = Array.from(selections.values());
      if (selArray.length === 0) {
        handleTap(nodeIndex);
        return;
      }

      let minDist = Infinity;
      const nearest: RangeSelection[] = [];

      for (const sel of selArray) {
        const dist = calculateDistance(nodeIndex, sel, punctuationMarkers);
        if (dist < minDist) {
          minDist = dist;
          nearest.length = 0;
          nearest.push(sel);
        } else if (dist === minDist) {
          nearest.push(sel);
        }
      }

      if (nearest.length === 0 || minDist >= 1000) {
        handleTap(nodeIndex);
        return;
      }

      const allIndices = [nodeIndex];
      for (const sel of nearest) {
        for (let i = sel.startIndex; i <= sel.endIndex; i++) allIndices.push(i);
      }
      allIndices.sort((a, b) => a - b);

      let startIndex = allIndices[0];
      let endIndex = allIndices[allIndices.length - 1];

      const isBackwards = nodeIndex === startIndex;
      const boundaries = findSentenceBoundaries(
        isBackwards ? nodeIndex : startIndex,
        endIndex,
        punctuationMarkers
      );

      if (boundaries.length > 0) {
        if (isBackwards) {
          startIndex = boundaries[0] + 1;
        } else {
          endIndex = boundaries[0];
        }
      }

      const newSel = createSelection(startIndex, endIndex);

      if (nearest.length === 1) {
        applyResult({ action: 'extend', oldSelection: nearest[0], newSelection: newSel });
      } else {
        applyResult({
          action: 'bridge',
          leftSelection: nearest[0],
          rightSelection: nearest[nearest.length - 1],
          mergedSelection: newSel,
        });
      }
    },
    [selections, punctuationMarkers, handleTap, createSelection, applyResult]
  );

  const updateTranslation = useCallback(
    (selectionId: string, translation: string, state: TranslationState) => {
      setSelections((prev) => {
        const sel = prev.get(selectionId);
        if (!sel) return prev;
        const next = new Map(prev);
        next.set(selectionId, { ...sel, translation, translationState: state });
        return next;
      });
    },
    []
  );

  return {
    selections,
    handleTap,
    handleLongPress,
    updateTranslation,
    getSelectionContaining,
  };
}
